import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock, ChefHat, Heart, Search, X } from 'lucide-react';
import { recipes, symptomFilters, ageFilters, genderFilters } from '../data/mock';
import { useKit } from '../hooks/useKit';
import { useProfiles } from '../contexts/ProfileContext';
import ItemPhoto from '../components/ItemPhoto';
import { warmupRecipes } from '../utils/photos';

function FilterRow({ options, value, onChange, allLabel = 'All' }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => onChange('all')}
        className={`px-3 py-1.5 rounded-full text-xs transition-colors capitalize ${
          value === 'all' ? 'bg-botanica-green text-white font-medium' : 'bg-botanica-card/60 text-botanica-muted hover:text-botanica-ink'
        }`}
      >
        {allLabel}
      </button>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`px-3 py-1.5 rounded-full text-xs transition-colors capitalize ${
            value === o ? 'bg-botanica-green text-white font-medium' : 'bg-botanica-card/60 text-botanica-muted hover:text-botanica-ink'
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function Recipes() {
  const [symptom, setSymptom] = useState('all');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { isSaved, toggle } = useKit();
  const { activeProfile, filterRecipes } = useProfiles();

  useEffect(() => {
    warmupRecipes();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const bySymptom = recipes.filter((r) => {
      const matchesSymptom = symptom === 'all' || r.symptoms.some((s) => s.toLowerCase().includes(symptom.toLowerCase())) || (symptom === 'sleep' && r.symptoms.includes('insomnia'));
      const matchesQuery = !q
        || r.name.toLowerCase().includes(q)
        || (r.type && r.type.toLowerCase().includes(q))
        || r.symptoms.some((s) => s.toLowerCase().includes(q));
      return matchesSymptom && matchesQuery;
    });
    return filterRecipes(bySymptom);
  }, [symptom, query, filterRecipes]);

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 min-h-[70vh]">
      <h1 className="font-serif text-4xl text-botanica-ink mb-2">Recipes</h1>
      {activeProfile ? (
        <p className="text-sm text-botanica-muted mb-6">
          Personalised for <span className="text-botanica-greenDark font-medium">{activeProfile.first_name}</span> — switch profiles from the menu, top right.
        </p>
      ) : (
        <p className="text-sm text-botanica-muted mb-6">
          Tip: <Link to="/profile" className="text-botanica-green underline">create a profile</Link> to personalise recipes to your age, sex &amp; conditions.
        </p>
      )}

      <div className="space-y-4 mb-6">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-botanica-muted absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search recipes by name, type or symptom…"
            className="w-full bg-botanica-card/70 border border-botanica-card rounded-full pl-11 pr-10 py-2.5 text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:border-botanica-green/40 transition-colors"
            data-testid="recipe-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-botanica-panel text-botanica-muted transition-colors"
              data-testid="recipe-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-botanica-muted font-semibold mb-2">Symptom</p>
          <FilterRow options={symptomFilters} value={symptom} onChange={setSymptom} allLabel="All Symptoms" />
        </div>
      </div>

      <p className="text-sm text-botanica-muted mb-4">{filtered.length} recipes</p>

      {filtered.length === 0 ? (
        <p className="text-sm text-botanica-muted py-16 text-center">No recipes match these filters. Try widening your search.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => {
            const saved = isSaved(r.id);
            return (
              <button
                key={r.id}
                onClick={() => navigate(`/recipes/${r.id}`)}
                className="group text-left bg-botanica-card/70 hover:bg-botanica-card rounded-2xl border border-transparent hover:border-botanica-green/20 transition-all hover:-translate-y-0.5 overflow-hidden"
              >
                <ItemPhoto itemId={r.id} alt={r.name} className="w-full h-36" />
                <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-lg text-botanica-ink leading-tight">{r.name}</h3>
                  <span
                    onClick={(e) => { e.stopPropagation(); toggle({ id: r.id, name: r.name, type: 'recipe', subtitle: `${r.type} · ${r.time}` }); }}
                    className="p-1.5 rounded-full hover:bg-botanica-panel transition-colors shrink-0"
                  >
                    <Heart className={`w-4 h-4 ${saved ? 'fill-botanica-green text-botanica-green' : 'text-botanica-muted'}`} />
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-botanica-muted mt-2">
                  <span className="px-2 py-0.5 rounded-full bg-botanica-panel">{r.type}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time}</span>
                  <span className="flex items-center gap-1"><ChefHat className="w-3 h-3" /> {r.difficulty}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {r.symptoms.map((s) => (
                    <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-botanica-greenLight text-botanica-greenDark">{s}</span>
                  ))}
                </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
